// File Metadata:
// Filename: choresRtkApi.js  
// Version: 3.00
// Created: 2025-06-16
// Last Modified: 2026-07-14
// Authors: Dieuwer Descheemaecker, GitHub Copilot
// Description: RTK Query API slice for chores operations with caching and optimistic updates.
//   v3.00 - Unified model: all chores (including recurring instances) use single Chore collection.
//           Removed dual chores/instances handling, type query params, and ChoreInstance tags.

import { baseApi } from '../../../store/api/baseApi';
import { authApi } from '../../Auth/store/authApi';

export const choresRtkApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Core Chore Endpoints
    getAllChores: builder.query({      
      query: (params = {}) => {
        const { familyId, ...otherParams } = params;
        if (!familyId) {
          throw new Error('familyId is required for getAllChores');
        }
        return {
          url: '/chores',
          params: { familyId, ...otherParams },
        };
      },
      providesTags: (result) => {
        const choresList = result?.chores || [];
        return choresList.length > 0
          ? [
              ...choresList.map(({ _id }) => ({ type: 'Chore', id: _id })),
              { type: 'Chore', id: 'LIST' }
            ]
          : [{ type: 'Chore', id: 'LIST' }];
      },
      keepUnusedDataFor: 120, // 2 minutes cache for chores list
    }),

    getUserChores: builder.query({
      query: ({ userId, ...params }) => ({
        url: `/chores/user/${userId}`,
        params,      }),      providesTags: (result, error, { userId }) => {
        const choresList = result?.chores || [];
        return choresList.length > 0
          ? [
              ...choresList.map(({ _id }) => ({ type: 'Chore', id: _id })),
              { type: 'Chore', id: `USER-${userId}` }
            ]
          : [{ type: 'Chore', id: `USER-${userId}` }];
      },
      keepUnusedDataFor: 120,
    }),

    getChoreById: builder.query({
      query: ({ id }) => ({
        url: `/chores/${id}`,
      }),
      providesTags: (result, error, { id }) => [{ type: 'Chore', id }],
    }),

    // Chore Mutations
    createChore: builder.mutation({
      query: (choreData) => ({
        url: '/chores',
        method: 'POST',
        body: choreData,
      }),
      invalidatesTags: [{ type: 'Chore', id: 'LIST' }],      // Optimistic update
      async onQueryStarted(choreData, { dispatch, queryFulfilled }) {
        const familyId = choreData.familyId;
        if (!familyId) return;
          const patchResult = dispatch(
          choresRtkApi.util.updateQueryData('getAllChores', { familyId }, (draft) => {
            const optimisticChore = {
              _id: `temp-${Date.now()}`,
              ...choreData,
              createdAt: new Date().toISOString(),
            };
            if (draft?.chores) {
              draft.chores.push(optimisticChore);
            }
          })
        );
        try {
          const { data: newChore } = await queryFulfilled;
          dispatch(
            choresRtkApi.util.updateQueryData('getAllChores', { familyId }, (draft) => {
              if (draft?.chores) {
                const index = draft.chores.findIndex(chore => chore._id.toString().startsWith('temp-'));
                if (index !== -1) {
                  draft.chores[index] = newChore;
                }
              }
            })
          );
        } catch {
          patchResult.undo();
        }
      },
    }),

    updateChore: builder.mutation({
      query: ({ id, updateData, scope }) => ({
        url: `/chores/${id}`,
        method: 'PUT',
        body: updateData,
        params: { scope },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Chore', id },
        { type: 'Chore', id: 'LIST' }
      ],
      async onQueryStarted({ id, updateData, familyId }, { dispatch, queryFulfilled }) {
        if (!familyId) return;

        // Serialize any Date objects to ISO strings to keep Redux state serializable
        const safeData = { ...updateData };
        for (const key of Object.keys(safeData)) {
          if (safeData[key] instanceof Date) {
            safeData[key] = safeData[key].toISOString();
          }
        }

        const patchResult = dispatch(
          choresRtkApi.util.updateQueryData('getAllChores', { familyId }, (draft) => {
            if (draft?.chores) {
              const chore = draft.chores.find(chore => chore._id === id);
              if (chore) {
                Object.assign(chore, safeData);
              }
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    deleteChore: builder.mutation({
      query: ({ id, scope }) => ({
        url: `/chores/${id}`,
        method: 'DELETE',
        params: { scope },
      }),
      invalidatesTags: [{ type: 'Chore', id: 'LIST' }],
      async onQueryStarted({ id, familyId }, { dispatch, queryFulfilled }) {
        if (!familyId) return;

        const patchResult = dispatch(
          choresRtkApi.util.updateQueryData('getAllChores', { familyId }, (draft) => {
            if (draft?.chores) {
              const index = draft.chores.findIndex(chore => chore._id === id);
              if (index !== -1) {
                draft.chores.splice(index, 1);
              }
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // Chore Action Endpoints
    completeChore: builder.mutation({
      query: ({ id, data }) => ({
        url: `/chores/${id}/complete`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Chore', id },
        { type: 'Chore', id: 'LIST' },
        // Invalidate user data to refresh points for assigned users
        { type: 'User', id: 'LIST' },
        'User'
      ],
      async onQueryStarted({ id, data, familyId }, { dispatch, queryFulfilled }) {
        if (!familyId) return;
        
        let chorePoints = 0;
        let assignedUserId = null;
        let requiresVerification = false;
        
        const patchResult = dispatch(
          choresRtkApi.util.updateQueryData('getAllChores', { familyId }, (draft) => {
            if (draft?.chores) {
              const chore = draft.chores.find(chore => chore._id === id);
              if (chore) {
                chorePoints = chore.points || 0;
                requiresVerification = !!chore.requiresVerification;
                assignedUserId = chore.assignedTo?._id || chore.assignedTo?.id || chore.assignedTo;
                chore.status = requiresVerification ? 'completed' : 'verified';
                chore.completedAt = new Date().toISOString();
                if (!requiresVerification) chore.verifiedAt = new Date().toISOString();
                if (data.photoProof) chore.photoProof = data.photoProof;
                if (data.notes) chore.notes = data.notes;
              }
            }
          })
        );
        
        // Only award points optimistically if the chore does NOT require verification
        // (verification-required chores get points when verified, not when completed)
        let userPatchResult;
        if (chorePoints > 0 && assignedUserId && !requiresVerification) {
          const currentUserData = authApi.endpoints.getMe.select()(dispatch.getState());
          const currentUserId = currentUserData.data?._id;
          
          if (currentUserId === assignedUserId) {
            userPatchResult = dispatch(
              authApi.util.updateQueryData('getMe', undefined, (draft) => {
                if (draft && typeof draft.points === 'number') {
                  draft.points += chorePoints;
                }
              })
            );
          }
        }
        
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          if (userPatchResult) userPatchResult.undo();
        }
      },
    }),

    verifyChore: builder.mutation({
      query: ({ id, data }) => ({
        url: `/chores/${id}/verify`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Chore', id },
        { type: 'Chore', id: 'LIST' },
        // Invalidate user data to refresh points for assigned users
        { type: 'User', id: 'LIST' },
        'User'
      ],
      async onQueryStarted({ id, data, familyId }, { dispatch, queryFulfilled }) {
        if (!familyId) return;
        
        let pointsChange = 0;
        let assignedUserId = null;
        
        const patchResult = dispatch(
          choresRtkApi.util.updateQueryData('getAllChores', { familyId }, (draft) => {
            if (draft?.chores) {
              const chore = draft.chores.find(chore => chore._id === id);
              if (chore) {
                assignedUserId = chore.assignedTo?._id || chore.assignedTo?.id || chore.assignedTo;
                if (data.approved) {
                  chore.status = 'verified';
                  chore.verifiedAt = new Date().toISOString();
                  if (data.feedback) chore.feedback = data.feedback;
                  if (data.adjustedPoints !== undefined) {
                    pointsChange = data.adjustedPoints;
                    chore.points = data.adjustedPoints;
                  } else {
                    pointsChange = chore.points || 0;
                  }
                } else {
                  // Rejection: reset to pending — no points to deduct
                  pointsChange = 0;
                  chore.status = 'pending';
                  chore.completedDate = null;
                  chore.photoProof = null;
                }
              }
            }
          })
        );
        
        // Optimistically update current user's points only if they are assigned to the chore
        let userPatchResult;
        if (pointsChange !== 0 && assignedUserId) {
          // First check if we need to update by getting current user data
          const currentUserData = authApi.endpoints.getMe.select()(dispatch.getState());
          const currentUserId = currentUserData.data?._id;
          
          // Only create the patch if the current user is the assigned user
          if (currentUserId === assignedUserId) {
            userPatchResult = dispatch(
              authApi.util.updateQueryData('getMe', undefined, (draft) => {
                if (draft && typeof draft.points === 'number') {
                  draft.points += pointsChange;
                }
              })
            );
          }
        }
        
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          if (userPatchResult) userPatchResult.undo();
        }
      },
    }),

    // Ensure recurring chore instances exist for a specific week
    ensureInstancesForWeek: builder.mutation({
      query: ({ familyId, startDate, endDate }) => ({
        url: '/chores/ensure-instances',
        method: 'POST',
        params: { familyId },
        body: { startDate, endDate },
      }),
      invalidatesTags: [
        { type: 'Chore', id: 'LIST' },
      ],
    }),

    // Upload photo proof for chore completion
    uploadPhotoProof: builder.mutation({
      query: (photoFile) => {
        const formData = new FormData();
        formData.append('photo', photoFile);
        return {
          url: '/chores/upload',
          method: 'POST',
          body: formData,
        };
      },
    }),

    // Analytics endpoint
    getChoreAnalytics: builder.query({
      query: (params = {}) => ({
        url: '/chores/analytics/data',
        params,
      }),
      providesTags: [{ type: 'Chore', id: 'ANALYTICS' }],
      keepUnusedDataFor: 300, // 5 minutes
    }),

    // Chore Template Endpoints
    getChoreTemplates: builder.query({
      query: (params = {}) => ({
        url: '/chores/templates',
        params,
      }),
      providesTags: (result) => {
        const templates = result?.templates || (Array.isArray(result) ? result : []);
        return templates.length > 0
          ? [
              ...templates.map(({ _id }) => ({ type: 'ChoreTemplate', id: _id })),
              { type: 'ChoreTemplate', id: 'LIST' },
            ]
          : [{ type: 'ChoreTemplate', id: 'LIST' }];
      },
      keepUnusedDataFor: 300, // 5 minutes - templates change infrequently
    }),

    getChoreTemplateById: builder.query({
      query: (id) => `/chores/templates/${id}`,
      providesTags: (result, error, id) => [{ type: 'ChoreTemplate', id }],
    }),

    createChoreTemplate: builder.mutation({
      query: (templateData) => ({
        url: '/chores/templates',
        method: 'POST',
        body: templateData,
      }),
      invalidatesTags: [{ type: 'ChoreTemplate', id: 'LIST' }],
    }),

    updateChoreTemplate: builder.mutation({
      query: ({ id, ...updateData }) => ({
        url: `/chores/templates/${id}`,
        method: 'PUT',
        body: updateData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ChoreTemplate', id },
        { type: 'ChoreTemplate', id: 'LIST' },
      ],
    }),

    deleteChoreTemplate: builder.mutation({
      query: (id) => ({
        url: `/chores/templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'ChoreTemplate', id },
        { type: 'ChoreTemplate', id: 'LIST' },
      ],
    }),

    rateChoreTemplate: builder.mutation({
      query: ({ id, ...ratingData }) => ({
        url: `/chores/templates/${id}/rate`,
        method: 'POST',
        body: ratingData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'ChoreTemplate', id }],
    }),

    createChoreFromTemplate: builder.mutation({
      query: ({ templateId, ...data }) => ({
        url: `/chores/templates/${templateId}/instantiate`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Chore', id: 'LIST' }],
    }),
  }),
});

// Export hooks for use in components
export const {
  // Core Chore Queries
  useGetAllChoresQuery,
  useLazyGetAllChoresQuery,
  useGetUserChoresQuery,
  useLazyGetUserChoresQuery,
  useGetChoreByIdQuery,
  
  // Chore Mutations
  useCreateChoreMutation,
  useUpdateChoreMutation,
  useDeleteChoreMutation,
  
  // Chore Actions
  useCompleteChoreMutation,
  useVerifyChoreMutation,
  
  // Recurring Chore Management
  useEnsureInstancesForWeekMutation,

  // Photo Upload
  useUploadPhotoProofMutation,

  // Analytics
  useGetChoreAnalyticsQuery,

  // Chore Templates
  useGetChoreTemplatesQuery,
  useLazyGetChoreTemplatesQuery,
  useGetChoreTemplateByIdQuery,
  useLazyGetChoreTemplateByIdQuery,
  useCreateChoreTemplateMutation,
  useUpdateChoreTemplateMutation,
  useDeleteChoreTemplateMutation,
  useRateChoreTemplateMutation,
  useCreateChoreFromTemplateMutation,
} = choresRtkApi;
